import moment from "moment";
import { useCallback, useEffect, useState } from "react";
import { PiCircle } from "react-icons/pi";
import { useParams } from "react-router-dom";
import { Text, Title, cn } from "rizzui";
import { toast } from "sonner";
import StatusDropdown from "../../../../../components/forms/StatusDropdown";
import { useContractRefresh } from "../../../../../contexts/ContractRefreshContext";
import useContractCommissionStates from "../../../../../hooks/api-calls/contract/useContractCommissionStates";
import useContractIncidenceStates from "../../../../../hooks/api-calls/contract/useContractIncidenceStates";
import useContractInvoiceStates from "../../../../../hooks/api-calls/contract/useContractInvoicingStates";
import useContractProductsStates from "../../../../../hooks/api-calls/contract/useContractProductsStates";
import useContractStates from "../../../../../hooks/api-calls/contract/useContractStates";
import useKoReasons from "../../../../../hooks/api-calls/useKoReasons";
import useFetch from "../../../../../hooks/useFetch";
import useHandleErrors from "../../../../../hooks/useHandleErrors";
import { useWindowDimensions } from "../../../../../hooks/useWindowDimensions";
import { ContractService } from "../../../../../services/contract/contractService";
import { ContractApiResponse } from "../../../../../type/entities/contract-type";
import ContractProfileLayout from "../../ContractProfileLayout";
import { usePrivilege } from "../../../../../components/priviledge/PriviledgeProvider";

const ContractStatesHistory = () => {

    const { id = '' } = useParams<{ id: string }>();
    const { width } = useWindowDimensions();
    const { handleErrors } = useHandleErrors();
    const { userCan } = usePrivilege()
    const { refreshTrigger } = useContractRefresh();
    const canChangeStatus = userCan('edit_contracts', 'contracts') && userCan('get_statuses_for_select', 'statuses');
    const { getContractStatesList, isLoadingContractStates } = useContractStates({ active: true, action: 'edit', entity: 'contract'});
    const { getContractProductsStatesList, isLoadingContractProductsStates } = useContractProductsStates({ active: true, action: 'edit', entity: 'contract_product'});
    const { getContractInvoiceStatesList, isLoadingContractInvoiceStates } = useContractInvoiceStates({ active: true, action: 'edit', entity: 'contract_invoicing'});
    const { getContractIncidenceStatesList, isLoadingContractIncidenceStates } = useContractIncidenceStates({ active: true, action: 'edit', entity: 'contract_incidence'});
    const { getContractCommissionStatesList, isLoadingContractCommissionStates } = useContractCommissionStates({ active: true, action: 'edit', entity: 'contract_commission'});
    const { koReasonsOptions } = useKoReasons();
    const service = new ContractService();

    const [timeline, setTimeline] = useState<any>(null);

    const [data, loading, error, refetch] = useFetch(useCallback(async () => {
        if (!id || id === '') return null;
        const response = await service.getContractById(id);
        const dataArray = response.getResponseData() ? [response.getResponseData() as ContractApiResponse] : [];
        return response.getResponseData() as ContractApiResponse;
    }, [id]));

    //-------------------------------------------------------------------------------------------------------------------------------
    /**
     * @ES CAMBIAR CUALQUIER ESTADO RELACIONADO CON CONTRATO
     * @EN CHANGE ANY STATUS RELATED TO CONTRACT
     */
    //-------------------------------------------------------------------------------------------------------------------------------
    const handleStateChange = async (id: string, stateId: string, comment: string, entityId: string, entityName: string, koReasonId?: string) => {
        var response = { success: false };
        switch (entityName) {
            case 'contract':
                try {
                    response = await (await service.changeContractStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del contrato actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'invoice':
                try {
                    response = await (await service.changeContractInvoicingStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la factura actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado');
                }
                break;
            case 'product':
                try {
                    response = await (await service.changeContractProductStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado del producto actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado del producto');
                }
                break;
            case 'incidence':
                try {
                    response = await (await service.changeContractIncidenceStatus(id, stateId, entityId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la incidencia actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de la incidencia');
                }
                break;
            case 'commission':
                try {
                    response = await (await service.changeContractCommissionStatus(id, stateId, comment, koReasonId)).getResponseData();
                    if (response.success) {
                        toast.success('Estado de la comisión actualizado correctamente');
                    } else {
                        handleErrors(response);
                    }
                } catch (error) {
                    toast.error('Error al actualizar el estado de la comisión');
                }
                break;
            default:
                break;
        }
        allData(); // Refresh the data after changing the status
    };
    //-------------------------------------------------------------------------------------------------------------------------------

    const allData = useCallback(async () => {
        if (!id) return null;

        const contractService = new ContractService();

        let entities = [
            { name: 'contract', label: 'Estados del contrato' },
            { name: 'contract_product', label: 'Estados del producto' },
            { name: 'contract_invoicing', label: 'Estados de facturación' },
            { name: 'contract_incidences', label: 'Estados de incidencia' },
            { name: 'contract_commissions', label: 'Estados de comisión' },
        ];

        const promises = entities.map(entity =>
            contractService
                .showContractStatusHistory({ filter_filters: { entity: entity.name, contract: id } })
                .then(res => ({
                    entity: entity.name,
                    label: entity.label,
                    data: res.getResponseData()?.data || [],
                }))
        );

        const results = await Promise.all(promises);

        // Join all results into a single array and sort by date
        const combined = results
            .flatMap(result =>
                result.data.map((item: any) => ({
                    ...item,
                    entity: result.entity,
                    label: result.label,
                }))
            )
            .sort((a, b) => new Date(a.createdAt.date).getTime() - new Date(b.createdAt.date).getTime())
            .reverse();

        // Set the timeline state
        setTimeline({ entities, combined });

        return { entities, combined };
    }, [id]);

    useEffect(() => {
        allData();
        refetch();
    }, [refreshTrigger]); // React to refreshTrigger changes

    return (
        <ContractProfileLayout isLoading={!timeline && !timeline?.entities && !timeline?.combined}>
            <div className="relative md:mt-4 w-full overflow-x-auto">
                <div className="md:min-w-[1400px] md:grid md:grid-cols-5 xl:grid-cols-5 3xl:grid-cols-5 md:divide-x-4 md:divide-gray-100 gap-y-12">
                    {timeline?.entities && timeline.entities.length > 0 &&
                        timeline.entities?.map((entity: any) => {
                            if (width < 768 && timeline?.combined.filter((t: any) => t.entity === entity.name).length === 0) return null;
                            return (
                                <div key={entity.name} className="px-4">
                                    <Title as="h2" className={cn("mt-8 mb-3 text-md font-semibold text-center text-gray-500", { "mt-10": width < 768 })}>
                                        {entity.label}
                                    </Title>
                                    <div className="mb-5">
                                        {entity.name === 'contract' && !isLoadingContractStates && (
                                            <StatusDropdown
                                                entityId={data?.id}
                                                disabled={!canChangeStatus}
                                                title="¿Estás seguro de cambiar el estado del contrato?"
                                                // Get the list of states for the current product type
                                                statesOptions={getContractStatesList(null, id)
                                                    .filter((state: any) => Boolean(data?.isDraft) || !state.draftStatus)}
                                                currentState={data?.currentContractStatus}
                                                currentStateDate={data?.currentContractStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                    handleStateChange(entityId, statusId, comment, data?.id, 'contract', koReasonId)
                                                }
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="contract"
                                            />
                                        )}
                                        {entity.name === 'contract_product' && data?.currentProductStatus?.id && data?.currentContractProductStatusDate?.date && (
                                            <StatusDropdown
                                                key={data?.currentProductStatus?.id}
                                                entityId={data?.id}
                                                disabled={!canChangeStatus}
                                                title="¿Estás seguro de cambiar el estado del producto?"
                                                // Get the list of states for the current product type
                                                statesOptions={getContractProductsStatesList(null, data?.currentProductStatus?.id)}
                                                currentState={data?.currentProductStatus}
                                                currentStateDate={data?.currentContractProductStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                    handleStateChange(entityId, statusId, comment, data?.product?.id, 'product', koReasonId)
                                                }
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="contract_product"
                                            />
                                        )}
                                        {entity.name === 'contract_invoicing' && data?.currentInvoicingStatus && (
                                            <StatusDropdown
                                                key={data?.currentInvoicingStatus?.id}
                                                entityId={data?.id}
                                                disabled={!data?.invoice?.id || !canChangeStatus}
                                                title="¿Estás seguro de cambiar el estado de la factura?"
                                                statesOptions={getContractInvoiceStatesList(null, data?.currentInvoicingStatus?.id)}
                                                currentState={data?.currentInvoicingStatus}
                                                currentStateDate={data?.currentContractInvoicingStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                    handleStateChange(entityId, statusId, comment, data?.invoice?.id ?? '', 'invoice', koReasonId)
                                                }
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="contract_invoicing"
                                            />
                                        )}
                                        {entity.name === 'contract_incidences' && data?.currentIncidenceStatus && (
                                            <StatusDropdown
                                                key={data?.currentIncidenceStatus?.id}
                                                entityId={data?.id}
                                                disabled={!data?.currentIncidence?.id || !canChangeStatus}
                                                title="¿Estás seguro de cambiar el estado de la incidencia?"
                                                statesOptions={getContractIncidenceStatesList(null, data?.currentIncidenceStatus?.id)}
                                                currentState={data?.currentIncidenceStatus}
                                                currentStateDate={data?.currentIncidence?.currentStatusDate?.date ?? data?.currentContractIncidenceStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                    handleStateChange(entityId, statusId, comment, data?.currentIncidence?.id ?? '', 'incidence', koReasonId)
                                                }
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="contract_incidence"
                                            />
                                        )}
                                        {entity.name === 'contract_commissions' && data?.currentCommissionStatus && data?.currentContractCommissionStatusDate?.date && (
                                            <StatusDropdown
                                                key={data?.currentCommissionStatus?.id}
                                                entityId={data?.id}
                                                disabled={!canChangeStatus}
                                                title="¿Estás seguro de cambiar el estado de la comisión?"
                                                // Get the list of states for the current product type
                                                statesOptions={getContractCommissionStatesList(null, data?.currentCommissionStatus?.id)}
                                                currentState={data?.currentCommissionStatus}
                                                currentStateDate={data?.currentContractCommissionStatusDate?.date}
                                                handleStateChange={(entityId: string, statusId: string, comment: string, koReasonId?: string) =>
                                                    handleStateChange(entityId, statusId, comment, '', 'commission', koReasonId)
                                                }
                                                koReasonsOptions={koReasonsOptions}
                                                koReasonStatusEntity="contract_commission"
                                            />
                                        )}
                                    </div>

                                    {timeline?.combined && timeline.combined.length > 0 &&
                                        timeline.combined?.map((t: any, index: number) => (
                                            <div
                                                className={cn(
                                                    "flex items-center ps-3",
                                                    ((t.entity !== entity.name) && width < 968) && "hidden"
                                                )}
                                                key={`t-${entity.name}-${index}`}
                                            >
                                                <div
                                                    className={cn(
                                                        "pe-2 flex-shrink-0 @lg:block",
                                                        t.entity !== entity.name && "opacity-0"
                                                    )}>
                                                    <Text as="span" className="pe-1 text-xs text-gray-500 font-medium @2xl:pe-1 text-nowrap">
                                                        {moment(t?.createdAt?.date).format('DD/MM/YYYY HH:mm')}
                                                    </Text>
                                                </div>
                                                <div
                                                    className={cn(
                                                        "relative flex-grow border-s border-muted py-5 ps-6 before:absolute before:-left-[3px] before:-top-[3px] before:h-1.5 before:w-1.5 before:rounded-full before:bg-gray-200 dark:border-gray-700 dark:before:bg-gray-900 rtl:before:-right-[3px]",
                                                        index !== 0 && "before:hidden",
                                                        index === t.combined?.length - 1 && "before:-bottom-[3px] before:top-auto before:block"
                                                    )}
                                                >
                                                    <span
                                                        className={cn(
                                                            "absolute -left-3 top-1/2 flex h-6 w-6 -translate-y-1/2 items-center justify-center rounded-full bg-gray-0 dark:bg-gray-50 rtl:-right-3",
                                                            t.entity !== entity.name && "opacity-20",
                                                            t.entity === entity.name && "text-primary-500 dark:text-primary-400"
                                                        )}
                                                    >
                                                        <PiCircle className="h-3 w-3" />
                                                    </span>
                                                    <Title
                                                        as="h3"
                                                        className={cn("mb-3 flex items-center text-xs font-semibold")}
                                                        style={{
                                                            color: t.entity === entity.name
                                                                ? t.color
                                                                : "transparent",
                                                        }}
                                                    >
                                                        {t.status}
                                                    </Title>
                                                    <div className="relative -ms-10">
                                                        <div className={cn("ps-10", t.entity !== entity.name && "opacity-0")}>
                                                            <Text
                                                                className={cn("block", {
                                                                    "text-xs text-gray-600 font-medium": t.entity === entity.name,
                                                                })}
                                                            >
                                                                {t.user}
                                                            </Text>
                                                            <Text className="text-xs font-normal text-gray-500">
                                                                {t.koReason ? (
                                                                    <span>
                                                                        <span className="font-medium text-gray-700">{t.koReason}</span>
                                                                        {t.koReasonDescription && <span className="block mt-0.5">{t.koReasonDescription}</span>}
                                                                    </span>
                                                                ) : (t.comment || '-')}
                                                            </Text>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    }
                                </div>
                            )
                        })}
                </div>
            </div>
        </ContractProfileLayout>
    );
};

export default ContractStatesHistory;
